/**
 * Base Code Composable
 * 기본 코드 조회를 위한 Vue 3 Composition API composable
 */

import { ref, computed } from 'vue';
import { baseCodeApi } from '../api/baseCodeApi';
import { useLanguageStore } from '../stores/languageStore';
import type { BaseCode } from '../types/baseCode.types';

/**
 * 기본 코드 조회 Composable
 */
export function useBaseCode() {
  const languageStore = useLanguageStore();
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * 현재 언어 코드
   */
  const currentLanguage = computed(() => languageStore.getCurrentLanguage());

  /**
   * 공통 API 호출 래퍼 (단일 코드 조회)
   */
  const fetchCode = async <T>(
    apiCall: () => Promise<any>,
    errorMessage: string
  ): Promise<T | null> => {
    try {
      loading.value = true;
      error.value = null;
      const response = await apiCall();
      // 응답 구조 통일: response.data?.code 또는 response.code
      return response.data?.code || response.code || null;
    } catch (err: any) {
      error.value = err;
      console.error(errorMessage, err);
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 공통 API 호출 래퍼 (코드 목록 조회)
   */
  const fetchCodes = async (
    apiCall: () => Promise<any>,
    errorMessage: string
  ): Promise<BaseCode[]> => {
    try {
      loading.value = true;
      error.value = null;
      const response = await apiCall();
      // 응답 구조 통일: response.codes || response.data?.codes
      return response.codes || response.data?.codes || [];
    } catch (err: any) {
      error.value = err;
      console.error(errorMessage, err);
      return [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * 특정 기본 코드 조회
   */
  const getCode = async (
    countryCode: string,
    totalCode: string,
    languageCode?: string
  ): Promise<BaseCode | null> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCode<BaseCode>(
      () => baseCodeApi.getCode(countryCode, totalCode, lang),
      'Failed to get base code'
    );
  };

  /**
   * TotalCode만으로 기본 코드 조회
   */
  const getCodeByTotalCode = async (
    countryCode: string,
    totalCode: string,
    languageCode?: string
  ): Promise<BaseCode | null> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCode<BaseCode>(
      () => baseCodeApi.getCodeByTotalCode(countryCode, totalCode, lang),
      'Failed to get base code by total code'
    );
  };

  /**
   * 메인 코드로 그룹 조회
   */
  const getCodesByMainCode = async (
    countryCode: string,
    mainCode: string,
    languageCode?: string
  ): Promise<BaseCode[]> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCodes(
      () => baseCodeApi.getCodesByMainCode(countryCode, mainCode, lang),
      'Failed to get base codes by main code'
    );
  };

  /**
   * MainCode만으로 기본 코드 목록 조회
   */
  const getCodesByMainCodeOnly = async (
    mainCode: string,
    languageCode?: string
  ): Promise<BaseCode[]> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCodes(
      () => baseCodeApi.getCodesByMainCodeOnly(mainCode, lang),
      'Failed to get base codes by main code only'
    );
  };

  /**
   * 업무용: TotalCode만으로 기본 코드 조회 (세션의 jurisdiction에서 countryCode 자동 추출)
   */
  const getCodeByTotalCodeAuto = async (
    totalCode: string,
    languageCode?: string
  ): Promise<BaseCode | null> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCode<BaseCode>(
      () => baseCodeApi.getCodeByTotalCodeAuto(totalCode, lang),
      'Failed to get base code by total code (auto jurisdiction)'
    );
  };

  /**
   * 업무용: MainCode로 기본 코드 목록 조회 (세션의 jurisdiction에서 countryCode 자동 추출)
   */
  const getCodesByMainCodeAuto = async (
    mainCode: string,
    languageCode?: string
  ): Promise<BaseCode[]> => {
    const lang = languageCode || currentLanguage.value;
    return fetchCodes(
      () => baseCodeApi.getCodesByMainCodeAuto(mainCode, lang),
      'Failed to get base codes by main code (auto jurisdiction)'
    );
  };

  return {
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    currentLanguage,
    getCode,
    getCodeByTotalCode,
    getCodesByMainCode,
    getCodesByMainCodeOnly,
    getCodeByTotalCodeAuto,
    getCodesByMainCodeAuto,
  };
}

/**
 * 기본 코드 옵션 목록으로 변환 (v-select 등에서 사용)
 * @param codes - 기본 코드 목록
 * @returns v-select items 형식
 */
export function baseCodesToOptions(codes: BaseCode[]) {
  return codes.map(code => ({
    title: code.codeName,
    value: code.totalCode,
    subtitle: `${code.mainCode}-${code.subCode}`,
    props: {
      codeName: code.codeName,
      defaultCodeName: code.defaultCodeName,
      countryCode: code.countryCode,
      mainCode: code.mainCode,
      subCode: code.subCode,
      orderBy: code.orderBy,
    }
  }));
}

